import merge from 'lodash/merge'
import type { Dispatch, SetStateAction } from 'react'
import React, { createContext, useContext as useContextCore } from 'react'
import type { ChatComponents } from './chatComponents'
import { chatComponents } from './chatComponents'
import { useToggle } from 'react-use'
export type InsertMessageFunction = (newMessage: string) => void

export type ProviderProps = {
  status: any
  // Optional: the Provider merges whatever is passed over `chatComponents`, so a caller happy with
  // every default omits it entirely. `ContextType.components` stays required — consumers read it
  // after that merge, never before.
  components?: ChatComponents
  message: string
  setMessage: (message: string) => void
  conversationId?: string
  onSend: (args: any) => Promise<void>
  messages: any[]
  middlewares?: any
  insertMessage?: InsertMessageFunction
  children: React.ReactNode
}

export type ContextType = {
  chatInputCursorPosition: number
  setChatInputCursorPosition: Dispatch<SetStateAction<number>>
  showEventsToo: boolean
  setShowEventsToo: Dispatch<SetStateAction<boolean>>
  error: any
  setError: Dispatch<SetStateAction<any>>
  components: ChatComponents
} & Omit<ProviderProps, 'children' | 'components'>

const context = createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

/**
 *
 * @param children
 * @param status - the sending, loading, error
 * @param components - includes the components that needs to be load, the default is chatOptions
 * @param message - the message that is displayed on the Input
 * @param setMessage - function to set the message
 * @param conversationId - the Id that is used to get information from the server
 * @param onSend - the action of sending message
 * @param messages - the messages from conversation
 * @param middlewares - functions that are executed from different parts
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export const Provider = ({
  children,
  components,
  insertMessage,
  message,
  setMessage,
  messages,
  conversationId,
  onSend,
  middlewares,
  ...props
}: ProviderProps) => {
  components = merge({}, chatComponents, components)
  const [showEventsToo, setShowEventsToo] = useToggle(false)

  const [chatInputCursorPosition, setChatInputCursorPosition] = React.useState<number>(0)
  insertMessage =
    insertMessage ??
    ((messageToInsert) => {
      //merge here using the substring
      //we make sure that the string is empty if not defined
      const newMessage = message ?? ''

      //now we got the text from zero to
      const start = newMessage.substring(0, chatInputCursorPosition ?? 0) ?? ''
      const mid = ` ${messageToInsert} ` //there is spaces on left and right
      const end = newMessage.substring(start?.length ?? 0, newMessage.length ?? 0) ?? ''

      setMessage?.(`${start}${mid}${end}`)
    })
  const [error, setError] = React.useState<any>(null)

  return (
    <CoreProvider
      value={{
        components,
        message,
        conversationId,
        onSend,
        messages,
        setMessage,
        chatInputCursorPosition,
        setChatInputCursorPosition,
        middlewares,
        insertMessage,
        showEventsToo,
        setShowEventsToo,
        error,
        setError,
        ...props,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useContext = () => useContextCore(context)
